import type { Patient, Reference } from '@medplum/fhirtypes'
import { invariant } from '@seen/core/invariant'
import { FacilityEncounter } from '@seen/core/models/encounters/facility-encounter'
import { Button } from '@seen/ui'
import type { JSX } from 'react'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogFooter } from '#src/components/core/dialog/DialogFooter.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { useTransactionMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useModalStore } from '#src/hooks/useModalStore.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

export function CheckOutDialog(): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const open = useModalStore((state) => state.checkOutOpen)
  const setOpen = useModalStore((state) => state.setCheckOutOpen)
  const participant = useModalStore((state) => state.participant)
  const setParticipant = useModalStore((state) => state.setParticipant)
  const { data: encounters = [] } = useSearch(
    FacilityEncounter,
    FacilityEncounter.byPatientInProgress(
      participant?.reference as Reference<Patient>
    ),
    {
      query: {
        enabled: !!participant,
      },
    }
  )

  const transaction = useTransactionMutation({
    mutation: {
      onSuccess: () => {
        setOpen(false)
        setParticipant(null)
      },
    },
  })

  const onSubmit = (): void => {
    transaction.mutate(async (bundle) => {
      invariant(participant, 'Participant is required')
      bundle.patch(participant, (participant) => {
        participant.isOnsite = false
      })
      encounters.forEach((encounter) => {
        bundle.patch(encounter, (encounter) => {
          encounter.finish()
        })
      })
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogDisplay>
        <DialogContent>Check out {participant?.name}?</DialogContent>
        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={isReadOnly}>
            Check out
          </Button>
        </DialogFooter>
      </DialogDisplay>
    </Dialog>
  )
}
